// -----------------------------------------------------------------------------------------------------------------
// Restricted - Copyright (C) Siemens Healthineers AG 2023.
// -----------------------------------------------------------------------------------------------------------------
import { __decorate } from 'tslib';
import { html } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { deviceIdentifier } from '../../utils/device-identifier';
import { event, ShuiLitElement } from '../../utils/event-decorator';
import { ActionClickedEvent, DismissedEvent, ShownEvent } from '../../utils/events';
import { keyboardInteraction } from '../../utils/keyboardInteraction';
import { KEYCODE } from '../../utils/keycode';
import snackbarStyles from './sh-snackbar.lit.scss.js';
/**
 * Modern Snackbar component - uncontrolled, parent manages visibility via DOM
 *
 * Usage:
 * - Parent adds/removes from DOM to show/hide
 * - CSS handles all animations automatically
 * - No internal state management for visibility
 */
/**
 * @fires action-clicked {ActionClickedEvent<SnackbarActionDetail>} - Emitted when the action button is clicked.
 * @fires dismissed {DismissedEvent<SnackbarDismissDetail>} - Emitted when the snackbar should be removed from DOM. Parent should listen to this and remove the element to trigger exit animation.
 * @fires shown {ShownEvent} - Emitted when the snackbar is visible.
 *
 */
let SHSnackbar = class SHSnackbar extends ShuiLitElement {
  constructor() {
    super(...arguments);
    /** Shows a close button that emits dismissed when activated. */
    this.dismissible = false;
    /** Auto-dismiss delay in ms; hover pauses, mouseleave resumes. */
    this.timeout = 4000;
    /** Placement within the render target with consistent edge spacing. */
    this.position = 'bottom-center';
    /** Where the snackbar renders; invalid ids warn and fallback to body. Moves without losing state. Parent container must have position relative when using `parent` or custom element targets. */
    this.renderTarget = 'body';
    /** Prevents recursive dismiss calls */
    this.isDismissing = false;
    this.clearDismissTimeout = () => {
      if (this.dismissTimeout) {
        clearTimeout(this.dismissTimeout);
        this.dismissTimeout = undefined;
        if (this.dismissStartTime) {
          const elapsed = Date.now() - this.dismissStartTime;
          this.remainingTimeout = Math.max(0, this.timeout - elapsed);
        }
      }
    };
    this.handleActionClick = () => {
      var _a;
      if (!((_a = this.actionLabel) === null || _a === void 0 ? void 0 : _a.trim())) return;
      this.actionClickedEvent.emit(
        new ActionClickedEvent({
          detail: { actionLabel: this.actionLabel },
          bubbles: true,
          composed: true,
        })
      );
    };
    this.handleCloseKeydown = (e) => {
      if (keyboardInteraction(e, KEYCODE.ENTER) || keyboardInteraction(e, KEYCODE.SPACE)) {
        this.handleDismiss();
        e.preventDefault();
      }
    };
    this.handleDismiss = () => {
      this.dismiss('user');
    };
  }
  static get styles() {
    return [snackbarStyles];
  }
  render() {
    var _a;
    return html`
      ${this.getIconTemplate()}
      ${this.label ? html`<label class="label">${this.label}</label>` : null}
      ${((_a = this.actionLabel) === null || _a === void 0 ? void 0 : _a.trim())
        ? html`<button
            class="action"
            @click="${this.handleActionClick}"
            tabindex="0"
            type="button"
            aria-label="${ifDefined(this.accessibleActionLabel || this.actionLabel)}"
          >
            ${this.actionLabel}
          </button>`
        : null}
      ${this.dismissible
        ? html`<sh-icon
            class="close-button"
            icon="cancel"
            size="s"
            button
            tabindex="0"
            aria-label="${this.accessibleCloseLabel || 'Dismiss notification'}"
            @click="${this.handleDismiss}"
            @keydown="${this.handleCloseKeydown}"
          ></sh-icon>`
        : null}
    `;
  }
  update(changedProperties) {
    this.setPositionClass();
    super.update(changedProperties);
  }
  updated(changedProperties) {
    super.updated(changedProperties);
    if (changedProperties.has('timeout')) {
      this.startDismissTimeout();
    }
    if (changedProperties.has('renderTarget')) {
      this.moveToRenderTarget();
    }
    if (changedProperties.has('type')) {
      this.updateAriaAttributes();
    }
    this.addTouchStartHandlerToActionButton();
  }
  firstUpdated(changedProperties) {
    super.firstUpdated(changedProperties);
    this.updateAriaAttributes();
    this.moveToRenderTarget();
    this.startDismissTimeout();
    if (!deviceIdentifier.isTouchDevice()) {
      this.addEventListener('mouseenter', this.clearDismissTimeout);
      this.addEventListener('mouseleave', this.startDismissTimeout);
    }
    requestAnimationFrame(() => {
      this.shownEvent.emit(new ShownEvent({ bubbles: true, composed: true }));
    });
  }
  disconnectedCallback() {
    this.removeEventListener('mouseenter', this.clearDismissTimeout);
    this.removeEventListener('mouseleave', this.startDismissTimeout);
    this.clearDismissTimeout();
    super.disconnectedCallback();
  }
  /**
   * Programmatically dismiss the snackbar.
   * This emits the dismissed event - parent should remove from DOM.
   */
  dismiss(reason = 'programmatic') {
    if (this.isDismissing) return;
    this.isDismissing = true;
    this.clearDismissTimeout();
    this.dismissedEvent.emit(
      new DismissedEvent({
        detail: { reason },
        bubbles: true,
        composed: true,
      })
    );
    this.isDismissing = false;
  }
  updateAriaAttributes() {
    const isError = this.type === 'error';
    this.setAttribute('role', isError ? 'alert' : 'status');
  }
  getIconName() {
    const typeIcons = {
      success: 'success',
      warning: 'warning',
      error: 'error',
      info: 'information',
    };
    return this.type ? typeIcons[this.type] : this.icon;
  }
  getIconTemplate() {
    const iconName = this.getIconName();
    if (!iconName) return undefined;
    return html`<sh-icon
      class="type-icon"
      size="s"
      icon="${iconName}"
      style="${this.iconColor ? `color: ${this.iconColor}` : ''}"
    ></sh-icon>`;
  }
  setPositionClass() {
    const positions = [
      'top-left',
      'top-center',
      'top-right',
      'bottom-left',
      'bottom-center',
      'bottom-right',
    ];
    this.classList.remove(...positions);
    if (this.position) {
      this.classList.add(this.position);
    }
  }
  startDismissTimeout() {
    var _a;
    this.clearDismissTimeout();
    const delay = (_a = this.remainingTimeout) !== null && _a !== void 0 ? _a : this.timeout;
    if (delay > 0) {
      this.dismissStartTime = Date.now();
      this.dismissTimeout = window.setTimeout(() => {
        this.dismiss('timeout');
      }, delay);
      this.remainingTimeout = undefined;
    }
  }
  moveToRenderTarget() {
    let target = null;
    if (this.renderTarget === 'body' || !this.renderTarget) {
      target = document.body;
      this.style.position = 'fixed';
    } else if (this.renderTarget === 'parent') {
      target = this.parentElement;
      this.style.position = 'absolute';
    } else {
      target = document.getElementById(this.renderTarget);
      if (!target) {
        console.warn(
          `[sh-snackbar] render-target '${this.renderTarget}' not found. Falling back to body.`
        );
        target = document.body;
        this.style.position = 'fixed';
      } else {
        this.style.position = 'absolute';
      }
    }
    if (target && this.parentNode !== target) {
      target.appendChild(this);
    }
  }
  addTouchStartHandlerToActionButton() {
    if (this.actionButton && !this.actionButton.hasAttribute('data-touchstart')) {
      this.actionButton.addEventListener('touchstart', () => {}, { passive: true });
      this.actionButton.setAttribute('data-touchstart', 'true');
    }
  }
};
__decorate([event()], SHSnackbar.prototype, 'actionClickedEvent', void 0);
__decorate([event()], SHSnackbar.prototype, 'dismissedEvent', void 0);
__decorate([event()], SHSnackbar.prototype, 'shownEvent', void 0);
__decorate([property({ type: String, reflect: true })], SHSnackbar.prototype, 'label', void 0);
__decorate(
  [property({ type: String, reflect: true, attribute: 'action-label' })],
  SHSnackbar.prototype,
  'actionLabel',
  void 0
);
__decorate([property({ type: String, reflect: true })], SHSnackbar.prototype, 'icon', void 0);
__decorate(
  [property({ type: String, reflect: true, attribute: 'icon-color' })],
  SHSnackbar.prototype,
  'iconColor',
  void 0
);
__decorate([property({ type: String, reflect: true })], SHSnackbar.prototype, 'type', void 0);
__decorate(
  [property({ type: Boolean, reflect: true })],
  SHSnackbar.prototype,
  'dismissible',
  void 0
);
__decorate([property({ type: Number, reflect: true })], SHSnackbar.prototype, 'timeout', void 0);
__decorate([property({ type: String, reflect: true })], SHSnackbar.prototype, 'position', void 0);
__decorate(
  [property({ type: String, reflect: true, attribute: 'render-target' })],
  SHSnackbar.prototype,
  'renderTarget',
  void 0
);
__decorate(
  [property({ type: String, reflect: true, attribute: 'accessible-close-label' })],
  SHSnackbar.prototype,
  'accessibleCloseLabel',
  void 0
);
__decorate(
  [property({ type: String, reflect: true, attribute: 'accessible-action-label' })],
  SHSnackbar.prototype,
  'accessibleActionLabel',
  void 0
);
__decorate([query('.action')], SHSnackbar.prototype, 'actionButton', void 0);
SHSnackbar = __decorate([customElement('sh-snackbar')], SHSnackbar);
export { SHSnackbar };
